import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Truck, Package, MapPin, Plus, ArrowRight, CheckCircle, Clock, AlertCircle, Loader2, FileText, Pencil, X, Save } from 'lucide-react';
import axios from 'axios';
import { DELIVERY_SLOTS, canModifyDelivery } from '../data/deliveryDates';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_MAP = {
  nouveau: { label: 'Nouveau', color: 'bg-blue-100 text-blue-700' },
  en_cours: { label: 'En cours', color: 'bg-amber-100 text-amber-700' },
  en_attente_virement: { label: 'Attente paiement', color: 'bg-green-100 text-green-700' },
  valide: { label: 'Validé', color: 'bg-green-100 text-green-700' },
  livre: { label: 'Livré', color: 'bg-emerald-100 text-emerald-800' },
  termine: { label: 'Terminé', color: 'bg-gray-100 text-gray-700' },
  annule: { label: 'Annulé', color: 'bg-red-100 text-red-700' },
};

const PRESTATION_MAP = {
  depot: 'Dépôt',
  express: 'Livraison Express',
  echange: 'Rotation',
  retrait: 'Retrait',
  passante: 'Chargement Express',
};

const PRESTATION_OPTIONS = [
  { value: 'depot', label: 'Dépôt' },
  { value: 'express', label: 'Livraison Express' },
  { value: 'echange', label: 'Rotation' },
  { value: 'retrait', label: 'Retrait' },
  { value: 'passante', label: 'Chargement Express' },
];

const VOLUME_OPTIONS = ['3m³', '8m³', '10m³', '15m³', '20m³', '30m³'];
const TYPE_OPTIONS = ['Mélange (DIB)', 'Végétaux', 'Terre', 'Gravat/terre', 'Bois', 'Cartons', 'Mobilier de bureau'];
const CRENEAU_OPTIONS = DELIVERY_SLOTS;

function canModify(benne) {
  if (['termine', 'livre', 'annule'].includes(benne.status)) return false;
  return canModifyDelivery(benne.date_debut, benne.creneau_horaire);
}

const EditModal = ({ benne, token, onClose, onSaved }) => {
  const [form, setForm] = useState({
    prestation: benne.prestation || '',
    benne_volume: benne.benne_volume || '',
    benne_type: benne.benne_type || '',
    date_debut: benne.date_debut || '',
    creneau_horaire: benne.creneau_horaire || '',
    contact_chantier: benne.contact_chantier || '',
    tel_contact_chantier: benne.tel_contact_chantier || '',
    commentaire: benne.commentaire || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API}/devis/${benne.id}?token=${token}`, form);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la modification');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="edit-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Pencil className="h-5 w-5 text-green-600" />
            Modifier commande #{benne.id}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Prestation</label>
            <select value={form.prestation} onChange={e => setForm({...form, prestation: e.target.value})}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-prestation">
              {PRESTATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Volume</label>
              <select value={form.benne_volume} onChange={e => setForm({...form, benne_volume: e.target.value})}
                className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-volume">
                {VOLUME_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Type de déchet</label>
              <select value={form.benne_type} onChange={e => setForm({...form, benne_type: e.target.value})}
                className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-type">
                {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Date de prestation</label>
              <Input type="date" value={form.date_debut} onChange={e => setForm({...form, date_debut: e.target.value})} data-testid="edit-date" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Créneau</label>
              <select value={form.creneau_horaire} onChange={e => setForm({...form, creneau_horaire: e.target.value})}
                className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-creneau">
                {CRENEAU_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                <option value="custom">Autre</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Contact chantier</label>
              <Input value={form.contact_chantier} onChange={e => setForm({...form, contact_chantier: e.target.value})}
                placeholder="Nom" data-testid="edit-contact" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Tél. contact</label>
              <Input value={form.tel_contact_chantier} onChange={e => setForm({...form, tel_contact_chantier: e.target.value})}
                placeholder="06..." data-testid="edit-tel" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Commentaire</label>
            <textarea value={form.commentaire} onChange={e => setForm({...form, commentaire: e.target.value})}
              className="w-full border rounded-lg px-3 py-2 text-sm h-20 resize-none" placeholder="Instructions..." data-testid="edit-commentaire" />
          </div>

          {error && <p className="text-red-500 text-sm bg-red-50 p-3 rounded-lg" data-testid="edit-error">{error}</p>}
        </div>

        <div className="flex gap-3 p-5 border-t bg-gray-50">
          <Button variant="outline" className="flex-1" onClick={onClose}>Annuler</Button>
          <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={handleSave} disabled={saving} data-testid="edit-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
};

const ClientDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null);
  const [editBenne, setEditBenne] = useState(null);

  const fetchData = () => {
    if (!user?.email || Number(user.is_admin) === 1) return;
    axios.get(`${API}/devis/user/${encodeURIComponent(user.email)}`)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [user]);

  if (Number(user?.is_admin) === 1) return <Navigate to="/admin" replace />;
  if (!user) { navigate('/connexion'); return null; }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  const stats = data?.stats || {};
  const chantiers = (data?.chantiers || []).map(c => ({
    ...c,
    bennes: statusFilter ? c.bennes.filter(b => {
      if (statusFilter === 'en_cours') return ['nouveau', 'en_cours', 'en_attente_virement'].includes(b.status);
      if (statusFilter === 'valide') return b.status === 'valide';
      if (statusFilter === 'termine') return ['termine', 'livre'].includes(b.status);
      return true;
    }) : c.bennes
  })).filter(c => c.bennes.length > 0);

  return (
    <div className="min-h-screen bg-gray-50 pt-32 pb-10 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mes Commandes</h1>
            <p className="text-sm text-gray-500">Bonjour {user.name}</p>
          </div>
          <Button onClick={() => navigate('/mon-compte')} className="bg-green-600 hover:bg-green-700 text-white" data-testid="new-order-btn">
            <Plus className="h-4 w-4 mr-2" />Commander
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total', value: stats.total || 0, icon: Package, color: 'text-blue-600', bg: 'bg-blue-50', filter: null },
            { label: 'En cours', value: stats.en_cours || 0, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', filter: 'en_cours' },
            { label: 'Validées', value: stats.valide || 0, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', filter: 'valide' },
            { label: 'Terminées', value: stats.termine || 0, icon: AlertCircle, color: 'text-gray-600', bg: 'bg-gray-50', filter: 'termine' },
          ].map(s => (
            <button key={s.label} onClick={() => setStatusFilter(statusFilter === s.filter ? null : s.filter)}
              className={`p-4 rounded-xl border transition-all ${statusFilter === s.filter ? 'ring-2 ring-green-500 border-green-300' : 'border-gray-200 hover:border-gray-300'} bg-white`}
              data-testid={`stat-${s.label.toLowerCase()}`}
            >
              <div className={`h-9 w-9 rounded-lg ${s.bg} flex items-center justify-center mb-2`}>
                <s.icon className={`h-4 w-4 ${s.color}`} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </button>
          ))}
        </div>

        {statusFilter && (
          <div className="flex items-center gap-2 mb-4">
            <Badge className="bg-green-100 text-green-700">Filtre: {statusFilter}</Badge>
            <button onClick={() => setStatusFilter(null)} className="text-xs text-red-500 hover:text-red-700 underline" data-testid="clear-filter-btn">
              Effacer le filtre
            </button>
          </div>
        )}

        {chantiers.length === 0 ? (
          <Card className="p-10 text-center" data-testid="no-orders">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Aucune commande</h3>
            <p className="text-gray-500 text-sm mb-4">Vous n'avez pas encore commandé de benne</p>
            <Button onClick={() => navigate('/mon-compte')} className="bg-green-600 hover:bg-green-700 text-white">
              <Plus className="h-4 w-4 mr-2" />Commander ma première benne
            </Button>
          </Card>
        ) : (
          <div className="space-y-6">
            {chantiers.map((chantier, idx) => (
              <Card key={idx} className="overflow-hidden" data-testid={`chantier-${idx}`}>
                <div className="bg-gray-50 px-5 py-3.5 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <MapPin className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm">{chantier.adresse}</h3>
                      <p className="text-xs text-gray-500">{chantier.total} benne{chantier.total > 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="border-green-300 text-green-700 hover:bg-green-50 text-xs"
                    onClick={() => navigate('/mon-compte', { state: { prefillAddress: chantier.adresse } })} data-testid={`chantier-${idx}-new-order`}>
                    <Plus className="h-3 w-3 mr-1" />Nouvelle benne ici
                  </Button>
                </div>

                <div className="divide-y divide-gray-100">
                  {chantier.bennes.map((benne) => {
                    const st = STATUS_MAP[benne.status] || STATUS_MAP.nouveau;
                    const modifiable = canModify(benne);
                    return (
                      <div key={benne.id} className="px-5 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-gray-50/50 transition-colors" data-testid={`benne-${benne.id}`}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                            <Truck className="h-5 w-5 text-green-600" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-sm text-gray-900">
                                {PRESTATION_MAP[benne.prestation] || benne.prestation}
                              </span>
                              <span className="text-xs text-gray-500">#{benne.id}</span>
                              <Badge className={`text-xs ${st.color}`}>{st.label}</Badge>
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {benne.benne_volume} - {benne.benne_type} | {benne.date_debut || '-'} {benne.creneau_horaire || ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:ml-auto flex-wrap">
                          <span className="text-xs text-gray-400">{benne.created_at}</span>
                          {modifiable && (
                            <Button size="sm" variant="outline" className="border-green-300 text-green-600 hover:bg-green-50 text-xs h-8"
                              onClick={() => setEditBenne(benne)} data-testid={`benne-${benne.id}-edit`}>
                              <Pencil className="h-3 w-3 mr-1" />Modifier
                            </Button>
                          )}
                          {benne.status === 'nouveau' && (
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs h-8"
                              onClick={() => navigate(`/paiement/${benne.id}`)} data-testid={`benne-${benne.id}-pay`}>
                              Payer<ArrowRight className="h-3 w-3 ml-1" />
                            </Button>
                          )}
                          {['valide', 'livre', 'termine'].includes(benne.status) && (
                            <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50 text-xs h-8"
                              onClick={() => window.open(`${API}/facture/${benne.id}?token=${token}`, '_blank')} data-testid={`benne-${benne.id}-facture`}>
                              <FileText className="h-3 w-3 mr-1" />Facture
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editBenne && (
        <EditModal benne={editBenne} token={token} onClose={() => setEditBenne(null)}
          onSaved={() => { setEditBenne(null); setLoading(true); fetchData(); }} />
      )}
    </div>
  );
};

export default ClientDashboard;
